// Raw REST fetch — bypassa il lock interno di supabase-js
// (navigator.locks può rimanere acquisito dopo crash/tab close → tutte le
// chiamate .from() si bloccano indefinitamente). Usiamo fetch diretto con il
// token da getSessionSafe (timeout + fallback cache).
//
// Retry one-shot su 401: se PostgREST risponde JWT expired (PGRST303),
// forziamo un refresh della sessione e riproviamo una sola volta.
import { supabase, getSessionSafe } from './supabase'
import useAuthStore from '../store/authStore'

function makeRequest(path, method, body, accessToken, prefer) {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/${path}`
  return fetch(url, {
    method,
    headers: {
      'Content-Type':  'application/json',
      'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Prefer':        prefer,
    },
    body: body ? JSON.stringify(body) : undefined,
  })
}

async function refreshAndRetry(path, method, body, prefer) {
  // Race con timeout di 8s perché supabase.auth.refreshSession() può bloccarsi
  // indefinitamente quando il tab rientra dal background (lock non rilasciato).
  try {
    const refreshed = await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('refresh timeout')), 8000)),
    ])
    const fresh = refreshed?.data?.session
    if (fresh?.access_token) {
      useAuthStore.setState({ session: fresh })
      return await makeRequest(path, method, body, fresh.access_token, prefer)
    }
  } catch (err) {
    console.warn('[dbFetch] refresh after 401 failed:', err?.message)
  }
  return null
}

async function execute(path, method, body, prefer) {
  const session = await getSessionSafe()
  if (!session?.access_token) throw new Error('Not authenticated')

  let res = await makeRequest(path, method, body, session.access_token, prefer)
  if (res.status === 401) {
    const retry = await refreshAndRetry(path, method, body, prefer)
    if (retry) res = retry
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    if (res.status === 401) throw new Error('session_expired')
    throw new Error(err.message || `HTTP ${res.status}`)
  }
  return res
}

/**
 * Fetch fire-and-forget. Niente body di ritorno (Prefer: return=minimal).
 * Usa per UPDATE/DELETE quando non ti serve sapere cosa è stato salvato.
 */
export async function dbFetch(path, method, body) {
  await execute(path, method, body, 'return=minimal')
}

/**
 * Fetch con response JSON. Usa per INSERT/SELECT quando ti servono i dati
 * (es. ID generato server-side, valori default applicati dal DB).
 */
export async function dbFetchJson(path, method, body) {
  const res = await execute(path, method, body, 'return=representation')
  if (res.status === 204) return null
  return res.json()
}
